import { CANONICAL_COMMANDS, normalizeCommandName } from '../core/commands/canonicalCommands';
import { useCommandManager } from '../state/commandStore';
import { useCommandPresetStore } from '../state/useCommandPresetStore';
import { useAppStore } from '../state/store';
// Direct import: the snapping barrel also exports the drei-based SnapIndicator,
// which would pin drei/fiber into this util's import graph.
import { useSnapStore } from '../tools/snapping/SnapManager';
import { normalizeShortcutString } from './revitShortcuts';

/**
 * Rhino Options (.ini) import
 *
 * Parses a McNeel Rhino "Rhino Full Options.ini" export (Options → Export)
 * and applies the two shortcut-bearing sections to the equivalent app
 * commands, so muscle memory from Rhino keeps working in the 3D workbench:
 *
 * - `[Options\AliasList]` — typed command aliases (`M=! _Move`). Each alias
 *   is registered twice: as the app keyboard shortcut (typed sequence) of
 *   the command the macro invokes, AND as a command-line name in the
 *   "Rhino Import" preset (activated on import), so typing the alias in the
 *   command line resolves to the same command.
 * - `[Options\ShortcutKeys]` — keyboard page macros (`Macro_N=...`). The ini
 *   stores macro strings indexed by a fixed slot number; the slot → key table
 *   below reconstructs the key combination each slot represents.
 *
 * All other sections (display modes, colors, paths, …) are app-managed and
 * intentionally ignored; their count is reported so nothing is silently lost.
 *
 * Command resolution reuses the canonical command table: its 3D family name
 * pool is spelled exactly like Rhino command names (`_Move`, `_BooleanUnion`,
 * `_SetDisplayMode`), so a macro's first command token maps through it.
 */

/** One parsed alias entry from `[Options\AliasList]`. */
export interface RhinoAliasEntry {
    /** Typed alias string ("M", "ZE", "Layeronall"). */
    alias: string;
    /** Raw Rhino macro the alias expands to (`! _Move`). */
    macro: string;
}

/** One parsed keyboard-page macro from `[Options\ShortcutKeys]`. */
export interface RhinoKeyEntry {
    /** Slot number from `Macro_N`. */
    slot: number;
    /** Raw Rhino macro stored in the slot. */
    macro: string;
}

/** One import binding successfully assigned to an app command. */
export interface RhinoImportApplied {
    commandId: string;
    commandLabel: string;
    shortcut: string;
    /** Primary Rhino command name the macro resolved to ("_Move"). */
    rhinoCommand: string;
    /** Which Rhino section produced the binding. */
    source: 'alias' | 'keyboard';
}

export type RhinoImportSkipReason =
    /** Macro command has no equivalent app command (or none registered yet). */
    | 'unmapped-command'
    /** Shortcut already claimed by an earlier entry in the same file. */
    | 'duplicate-shortcut'
    /** Command already received a (different) shortcut from the same file. */
    | 'duplicate-command'
    /** Keyboard macro sits in a slot whose key combination is not known. */
    | 'unmapped-key'
    /** Empty or control-only macro that names no command. */
    | 'unknown-macro';

export interface RhinoImportSkipped {
    shortcut: string;
    rhinoCommand: string;
    source: 'alias' | 'keyboard';
    reason: RhinoImportSkipReason;
}

/** A command outside the import whose effective shortcut equals an imported one. */
export interface RhinoImportCollision {
    commandId: string;
    commandLabel: string;
    shortcut: string;
}

export interface RhinoImportSummary {
    /** Alias entries parsed from `[Options\AliasList]`. */
    totalAliases: number;
    /** Non-empty macro entries parsed from `[Options\ShortcutKeys]`. */
    totalKeyboardMacros: number;
    /** Other ini sections present but not consumed (display modes, paths, …). */
    ignoredSections: number;
    applied: RhinoImportApplied[];
    skipped: RhinoImportSkipped[];
    collisions: RhinoImportCollision[];
    /** Alias strings registered as command-line names (preset overrides). */
    presetAliases: number;
    /** Canonical commands that received command-line alias overrides. */
    presetCommands: number;
    /** True when the import preset was created; false when replaced/absent. */
    presetCreated: boolean;
    /** Modeling-aid switches applied from `[Options\ModelAidSettings]`. */
    settingsApplied: string[];
    /** Names of the ini sections present but not consumed. */
    ignoredSectionNames: string[];
}

export class RhinoOptionsParseError extends Error {}

// ---------------------------------------------------------------------------
// INI parsing
// ---------------------------------------------------------------------------

/**
 * Read the picked ini file honoring its encoding. Rhino's Options → Export
 * writes UTF-16 LE (with BOM) on Windows, while `File.text()` always decodes
 * UTF-8 — so the BOM is sniffed from the raw bytes and the matching decoder
 * is used. Anything without a UTF-16 BOM falls back to UTF-8.
 */
export async function readRhinoOptionsFile(file: File): Promise<string> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (bytes[0] === 0xff && bytes[1] === 0xfe) {
        return new TextDecoder('utf-16le').decode(bytes);
    }
    if (bytes[0] === 0xfe && bytes[1] === 0xff) {
        return new TextDecoder('utf-16be').decode(bytes);
    }
    return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Parse a Windows-INI style file into `sectionName → key → value` records.
 * Tolerates a UTF-8 BOM, CRLF line endings, and duplicate keys (last wins).
 * Stray NUL characters (half-decoded UTF-16 without BOM handling) are
 * dropped so the section regexes still match instead of failing silently.
 */
function parseIni(text: string): Map<string, Record<string, string>> {
    const sections = new Map<string, Record<string, string>>();
    let current: Record<string, string> | null = null;

    for (const rawLine of text.replace(/^\uFEFF/, '').replace(/\0/g, '').split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!line) continue;
        if (line.startsWith('[') && line.endsWith(']')) {
            const name = line.slice(1, -1).trim();
            current = sections.get(name) ?? {};
            sections.set(name, current);
            continue;
        }
        if (!current) continue;
        const eq = line.indexOf('=');
        if (eq <= 0) continue;
        current[line.slice(0, eq).trim()] = line.slice(eq + 1).trim();
    }
    return sections;
}

// ---------------------------------------------------------------------------
// Command-name resolution
// ---------------------------------------------------------------------------

/**
 * Store-id index over the canonical command table: normalized Rhino/AutoCAD
 * command name → registered command ids. The 3D family pool is consulted
 * first (it is spelled like Rhino), then the 2D pool covers AutoCAD-style
 * aliases (`DLI`, `REC`, …).
 */
const RHINO_NAME_INDEX = new Map<string, string[]>();
for (const cmd of CANONICAL_COMMANDS) {
    const storeId = `cmd.${cmd.id}`;
    for (const pool of [cmd.names['3d'], cmd.names['2d']]) {
        for (const name of pool) {
            const key = normalizeCommandName(name);
            if (!key) continue;
            const bucket = RHINO_NAME_INDEX.get(key);
            if (bucket) {
                if (!bucket.includes(storeId)) bucket.push(storeId);
            } else {
                RHINO_NAME_INDEX.set(key, [storeId]);
            }
        }
    }
}

/**
 * Rhino command names that exist only outside the canonical table but have a
 * registered app command (plain ids, resolved before the name index).
 */
const RHINO_COMMAND_EXTRA: Record<string, string> = {
    UNDO: 'undo',
    REDO: 'redo',
    // Grasshopper is Rhino's visual scripting front-end.
    GRASSHOPPER: 'cmd.sys.visualScripting',
    // `_Extrude` in Rhino extrudes a curve; the canonical name is `_ExtrudeCrv`.
    EXTRUDE: 'cmd.srf.extrude',
    // File commands live on the legacy registry (File category).
    SAVE: 'saveProject',
    SAVEAS: 'saveProjectAs',
    NEW: 'newProject',
    OPEN: 'openProject',
    // Clipboard commands are legacy registry entries too.
    PASTE: 'paste',
    CUT: 'cut',
};

/**
 * Multi-token macro signatures whose meaning no single token captures
 * (`_Setview _World _Top` is "Top view", not the generic SetView dialog;
 * `_SetDisplayMode "Shaded"` is one concrete display mode, not the cycle
 * toggle). Keys are the first non-control macro tokens joined with spaces,
 * quoted arguments quote-stripped.
 */
const RHINO_MACRO_OVERRIDES: Record<string, string> = {
    'ZOOM ALL EXTENTS': 'cmd.view.zoomExtents',
    'ZOOM ALL SELECTED': 'cmd.view.zoomSelected',
    'SETVIEW WORLD TOP': 'cmd.view.top',
    'SETVIEW WORLD FRONT': 'cmd.view.front',
    'SETVIEW WORLD RIGHT': 'cmd.view.right',
    'SETVIEW WORLD PERSPECTIVE': 'cmd.view.perspective',
    'SETVIEW CPLANE TOP': 'cmd.view.top',
    'VIEWPORTPROPERTIES PROJECTION PERSPECTIVE': 'cmd.view.perspective',
    'SETDISPLAYMODE WIREFRAME': 'cmd.view.displayWireframe',
    'SETDISPLAYMODE SHADED': 'cmd.view.displayShaded',
    'SETDISPLAYMODE RENDERED': 'cmd.view.displayRendered',
    'SETDISPLAYMODE GHOSTED': 'cmd.view.displayGhosted',
    'SETDISPLAYMODE XRAY': 'cmd.view.displayXray',
    // Third-party "AD Realtime Renderer" plug-in mode: closest app capability
    // is the rendered display mode.
    'SETDISPLAYMODE ADREALTIMERENDERER': 'cmd.view.displayRendered',
    'SETDISPLAYMODE AD REALTIME': 'cmd.view.displayRendered',
    // Rhino options/document-properties pages with an app equivalent; the
    // remaining pages (Hatch, Linetypes, Dimensions, Render…) have none.
    'DOCUMENTPROPERTIESPAGE UNITS': 'cmd.sys.units',
    'OPTIONSPAGE PLUGINS': 'cmd.sys.appLoad',
};

/** Macro plumbing tokens that never name a command. */
const CONTROL_TOKENS = new Set(['NOECHO', 'ECHO', 'PAUSE', 'ENTER', 'ENTEREND']);

/**
 * Normalize one macro token into a bare command-name candidate. Strips the
 * Rhino macro prefix markers (`!` no-pause, `'` no-echo, `~` bottom-bar) and
 * surrounding quotes (quoted SetDisplayMode arguments), then the command
 * underline/dash forms (`_-Layer` → `LAYER`).
 */
function normalizeToken(token: string): string {
    return normalizeCommandName(token.replace(/^[!'~"]+/, '').replace(/"+$/, ''));
}

/** Split a macro into normalized command-name candidates, dropping plumbing.
 *  Quoted arguments are KEPT (quote-stripped) so multi-token signatures like
 *  `SetDisplayMode "Shaded"` can resolve to the specific display command. */
function extractCommandTokens(macro: string): string[] {
    return macro
        .split(/\s+/)
        .map(normalizeToken)
        .filter((t) => t.length > 0 && !CONTROL_TOKENS.has(t) && t !== '*');
}

/** One resolution candidate: a registered app command the macro may mean. */
interface MacroCandidate {
    commandId: string;
    /** Display spelling of the primary Rhino command token ("_Move"). */
    rhinoCommand: string;
}

/**
 * All registered app commands a macro could mean, in resolution-priority
 * order: compound three-token join, two-token join, multi-token override
 * signature, extras table, first token via the name index, exact label
 * match. Assignment filtering is the caller's concern — the keyboard pass
 * picks the first candidate not already claimed by the import, while the
 * command-line pass takes the most specific candidate regardless.
 */
function resolveMacroCandidates(
    macro: string,
    commands: Record<string, { id: string; label: string }>
): MacroCandidate[] {
    const tokens = extractCommandTokens(macro);
    if (tokens.length === 0) return [];

    const out: MacroCandidate[] = [];
    const push = (id: string | undefined) => {
        if (!id || !commands[id]) return;
        if (out.some((c) => c.commandId === id)) return;
        out.push({ commandId: id, rhinoCommand: `_${tokens[0]}` });
    };
    const pushIndexHits = (keys: string[]) => {
        for (const key of keys) {
            const ids = RHINO_NAME_INDEX.get(key);
            if (ids) {
                for (const id of ids) push(id);
                return;
            }
        }
    };

    const first3 = tokens.slice(0, 3);
    pushIndexHits([first3.join(''), tokens.slice(0, 2).join('')]);
    const override =
        RHINO_MACRO_OVERRIDES[first3.join(' ')] ?? RHINO_MACRO_OVERRIDES[first3.slice(0, 2).join(' ')];
    if (out.length === 0) push(override);
    push(RHINO_COMMAND_EXTRA[tokens[0]]);
    pushIndexHits([tokens[0]]);
    if (out.length === 0) {
        // Label fallback: "Extrude" → command labelled "Extrude".
        const label = tokens[0].toLowerCase();
        for (const cmd of Object.values(commands)) {
            if (cmd.label.toLowerCase() === label) {
                push(cmd.id);
                break;
            }
        }
    }
    return out;
}

// ---------------------------------------------------------------------------
// Keyboard slot → key table
// ---------------------------------------------------------------------------

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * `[Options\ShortcutKeys]` stores macros in numbered slots whose key
 * assignment is fixed by Rhino (the ini itself carries no key labels):
 * slots 0–23 are F1–F24, then one 26-slot group per modifier family —
 * Ctrl+A–Z, Ctrl+Shift+A–Z, Ctrl+Alt+A–Z. Verified against the stock Rhino
 * keyboard table twice: the Ctrl/Ctrl+Shift groups land on the documented
 * defaults (Copy/New/Open/Print/Save/Cut/Redo/Undo, Hide/Join/Lock/Trim),
 * and the Ctrl+Alt group puts all five display modes on their initials
 * (`Ctrl+Alt+G` Ghosted, `+R` Rendered, `+S` Shaded, `+W` Wireframe,
 * `+X` X-ray). Later slot ranges (modifier F-key variants, digit groups,
 * slots 126+) carry no verifiable key labels and stay unmapped rather than
 * inventing bindings; affected macros are reported as skipped.
 */
const KEY_SLOT_LABELS = new Map<number, string>();
for (let f = 1; f <= 24; f++) KEY_SLOT_LABELS.set(f - 1, `F${f}`);
const MODIFIER_GROUPS: Array<[number, string]> = [
    [48, 'Ctrl'],
    [74, 'Ctrl+Shift'],
    [100, 'Ctrl+Alt'],
];
for (const [base, prefix] of MODIFIER_GROUPS) {
    for (let i = 0; i < LETTERS.length; i++) {
        KEY_SLOT_LABELS.set(base + i, `${prefix}+${LETTERS[i]}`);
    }
}

/** Import section names consumed by the shortcut pipeline. */
const ALIAS_SECTION = 'Options\\AliasList';
const KEYS_SECTION = 'Options\\ShortcutKeys';

/** Fixed id of the preset that carries imported Rhino command-line names. */
export const RHINO_IMPORT_PRESET_ID = 'import.rhino';
export const RHINO_IMPORT_PRESET_NAME = 'Rhino Import';

// ---------------------------------------------------------------------------
// Import pipeline
// ---------------------------------------------------------------------------

/**
 * Import a Rhino Options .ini export: parse → resolve → apply. Aliases are
 * registered in two places: as user keyboard shortcuts on the command store
 * (persisted by zustand persist) and as command-line names in the "Rhino
 * Import" preset, which is created/replaced and activated on import.
 *
 * Rules: file order wins — the first entry for a shortcut (or for a command)
 * is applied and later duplicates are reported as skipped. Command-line
 * registration is independent of keyboard assignment: every alias that
 * resolves to a canonical command becomes a callable name, so several
 * aliases of one command all resolve. Assignments are still applied when
 * they shadow a command outside the import; those pairs are returned as
 * `collisions` for the caller to surface.
 */
export function importRhinoOptions(ini: string): RhinoImportSummary {
    const sections = parseIni(ini);
    if (sections.size === 0) {
        throw new RhinoOptionsParseError('The file contains no Rhino options sections.');
    }

    const { commands, updateShortcut } = useCommandManager.getState();

    const applied: RhinoImportApplied[] = [];
    const skipped: RhinoImportSkipped[] = [];
    const assigned = new Map<string, string>(); // commandId → shortcut
    const claimedShortcuts = new Set<string>(); // lowercase shortcut → claimed this run

    const applyKeyboard = (shortcut: string, macro: string, source: 'alias' | 'keyboard') => {
        const candidates = resolveMacroCandidates(macro, commands);
        if (candidates.length === 0) {
            skipped.push({ shortcut, rhinoCommand: macro, source, reason: 'unmapped-command' });
            return;
        }
        if (claimedShortcuts.has(shortcut.toLowerCase())) {
            skipped.push({
                shortcut,
                rhinoCommand: candidates[0].rhinoCommand,
                source,
                reason: 'duplicate-shortcut',
            });
            return;
        }
        const resolved = candidates.find((c) => !assigned.has(c.commandId));
        if (!resolved) {
            skipped.push({
                shortcut,
                rhinoCommand: candidates[0].rhinoCommand,
                source,
                reason: 'duplicate-command',
            });
            return;
        }
        const command = commands[resolved.commandId];
        updateShortcut(resolved.commandId, shortcut);
        assigned.set(resolved.commandId, shortcut);
        claimedShortcuts.add(shortcut.toLowerCase());
        applied.push({
            commandId: resolved.commandId,
            commandLabel: command.label,
            shortcut,
            rhinoCommand: resolved.rhinoCommand,
            source,
        });
    };

    // --- [Options\AliasList] — typed aliases become typed sequences --------
    const aliasSection = sections.get(ALIAS_SECTION) ?? {};
    const aliasIndex = new Map<string, string>(); // lowercase alias → macro
    for (const [alias, macro] of Object.entries(aliasSection)) {
        aliasIndex.set(alias.toLowerCase(), macro);
    }
    // Aliases may point at other aliases (one indirection pass, cycle-safe).
    const resolveAliasMacro = (alias: string, macro: string): string => {
        const parts = macro.trim().split(/\s+/);
        if (parts.length === 1) {
            const target = aliasIndex.get(parts[0].toLowerCase());
            if (target && parts[0].toLowerCase() !== alias.toLowerCase()) return target;
        }
        return macro;
    };

    // --- Alias strings → command-line names (active preset overrides) -----
    // Independent of keyboard assignment: every alias that resolves to a
    // canonical command is registered as a callable command-line name, so
    // several aliases of one Rhino command (WF/SD/XR → SetDisplayMode) all
    // resolve when typed, with the file's first spelling as the primary.
    const presetOverrides: Record<string, string[]> = {};
    for (const [alias, macro] of Object.entries(aliasSection)) {
        if (!macro.trim()) continue;
        const candidates = resolveMacroCandidates(resolveAliasMacro(alias, macro), commands);
        const canonical = candidates.find((c) => c.commandId.startsWith('cmd.'));
        if (!canonical) continue; // Legacy-only targets (UNDO/REDO) stay keyboard-only.
        const canonicalId = canonical.commandId.slice('cmd.'.length);
        const names = presetOverrides[canonicalId];
        if (names) names.push(alias.trim());
        else presetOverrides[canonicalId] = [alias.trim()];
    }
    const presetResult =
        Object.keys(presetOverrides).length > 0
            ? useCommandPresetStore.getState().upsertImportedPreset({
                  id: RHINO_IMPORT_PRESET_ID,
                  name: RHINO_IMPORT_PRESET_NAME,
                  overrides: presetOverrides,
                  suggestionPriority: ['3d', '2d'],
                  baseFamily: '3d',
                  notes: 'Imported from a Rhino Options ini: aliases are the primary command-line names; built-in names stay callable. Re-import replaces these overrides.',
              })
            : null;

    for (const [alias, macro] of Object.entries(aliasSection)) {
        const shortcut = normalizeShortcutString(alias);
        if (!macro.trim()) continue; // Unbound alias row.
        if (!shortcut) continue;
        applyKeyboard(shortcut, resolveAliasMacro(alias, macro), 'alias');
    }

    // --- [Options\ShortcutKeys] — keyboard-page macros ----------------------
    const keySection = sections.get(KEYS_SECTION) ?? {};
    const slotPattern = /^Macro_(\d+)$/;
    for (const [key, macro] of Object.entries(keySection)) {
        if (!macro.trim()) continue; // Empty keyboard slot.
        const slotMatch = slotPattern.exec(key);
        if (!slotMatch) continue;
        const slot = Number.parseInt(slotMatch[1], 10);
        const keyLabel = KEY_SLOT_LABELS.get(slot);
        if (!keyLabel) {
            skipped.push({
                shortcut: `slot ${slot}`,
                rhinoCommand: '',
                source: 'keyboard',
                reason: 'unmapped-key',
            });
            continue;
        }
        const shortcut = normalizeShortcutString(keyLabel);
        applyKeyboard(shortcut, macro, 'keyboard');
    }

    // --- [Options\ModelAidSettings] — modeling-aid switches the app owns ---
    // Only the switches with a real app counterpart are applied (grid snap,
    // ortho); the section's remaining fields (nudge steps, osnap bitmask,
    // pickbox radii) have no app-side model and stay untouched.
    const settingsApplied: string[] = [];
    const aidSection = sections.get('Options\\ModelAidSettings') ?? {};
    const parseYesNo = (value: string | undefined): boolean | null => {
        const v = value?.trim().toLowerCase();
        return v === 'y' ? true : v === 'n' ? false : null;
    };
    const gridSnap = parseYesNo(aidSection['grid_snap_on']);
    if (gridSnap !== null) {
        useSnapStore.getState().setGridSnapEnabled(gridSnap);
        settingsApplied.push(`grid snap ${gridSnap ? 'on' : 'off'}`);
    }
    const ortho = parseYesNo(aidSection['ortho_on']);
    if (ortho !== null) {
        const app = useAppStore.getState();
        if (app.orthoMode !== ortho) app.toggleOrthoMode();
        settingsApplied.push(`ortho ${ortho ? 'on' : 'off'}`);
    }

    // Effective-shortcut collisions with commands the import did not touch.
    const collisions: RhinoImportCollision[] = [];
    const finalCommands = useCommandManager.getState().commands;
    for (const cmd of Object.values(finalCommands)) {
        if (assigned.has(cmd.id)) continue;
        const effective = (cmd.shortcut ?? cmd.defaultShortcut ?? '').trim().toLowerCase();
        if (!effective) continue;
        for (const [commandId, shortcut] of assigned) {
            if (shortcut.toLowerCase() === effective) {
                collisions.push({ commandId: cmd.id, commandLabel: cmd.label, shortcut });
                break;
            }
        }
    }

    const consumed = new Set([ALIAS_SECTION, KEYS_SECTION, 'Options\\ModelAidSettings']);
    return {
        totalAliases: Object.keys(aliasSection).length,
        totalKeyboardMacros: Object.entries(keySection).filter(
            ([key, macro]) => macro.trim() !== '' && slotPattern.test(key)
        ).length,
        ignoredSections: sections.size - [...consumed].filter((name) => sections.has(name)).length,
        ignoredSectionNames: [...sections.keys()].filter((name) => !consumed.has(name)),
        applied,
        skipped,
        collisions,
        presetAliases: Object.values(presetOverrides).reduce((n, names) => n + names.length, 0),
        presetCommands: Object.keys(presetOverrides).length,
        presetCreated: presetResult?.created ?? false,
        settingsApplied,
    };
}
